import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';

import { Close } from '../assets/Icons';
import logo from '../assets/logo.svg';
import useServiceList from '../hooks/useServiceList';

interface Props {
  buttonClassname: string;
  closeMenu: () => void;
  currentPath?: string;
  isOpen: boolean;
}

const MobileNavigation: React.FC<Props> = ({
  buttonClassname,
  closeMenu,
  currentPath,
  isOpen,
}) => {
  const services = useServiceList();
  const $dialogRef = useRef<HTMLDivElement>(null);
  const $closeButtonRef = useRef<HTMLButtonElement>(null);

  function focusCloseButton() {
    $closeButtonRef.current?.focus();
  }

  useEffect(() => {
    if (!isOpen) return;

    const $previouslyFocused = document.activeElement as HTMLElement | null;
    focusCloseButton();
    window.requestAnimationFrame(focusCloseButton);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeMenu();
        return;
      }

      if (event.key !== 'Tab' || !$dialogRef.current) return;

      const focusableElements =
        $dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
      const $first = focusableElements[0];
      const $last = focusableElements[focusableElements.length - 1];

      if (!$first || !$last) return;

      if (event.shiftKey && document.activeElement === $first) {
        event.preventDefault();
        $last.focus();
      } else if (!event.shiftKey && document.activeElement === $last) {
        event.preventDefault();
        $first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      $previouslyFocused?.focus();
    };
  }, [closeMenu, isOpen]);

  return (
    <Transition
      afterEnter={focusCloseButton}
      enter="transition duration-200 ease-out"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="transition duration-100 ease-in"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
      show={isOpen}
    >
      <div
        aria-labelledby="mobile-navigation-title"
        aria-modal="true"
        className="absolute inset-x-0 top-0 z-20 origin-top-right transform p-2 transition md:hidden"
        id="mobile-navigation"
        role="dialog"
      >
        <div
          className="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          ref={$dialogRef}
        >
          <span className="sr-only" id="mobile-navigation-title">
            Glavna navigacija
          </span>
          <div className="px-5 pb-6 pt-5">
            <div className="flex items-center justify-between">
              <Link onClick={closeMenu} to="/">
                <img
                  alt="Keso Gradnja logo"
                  className="h-11 w-auto"
                  src={logo}
                />
              </Link>

              <div className="-mr-2">
                <button
                  aria-label="Zatvori glavnu navigaciju"
                  className={clsx(
                    buttonClassname,
                    'text-gray-400 hover:text-gray-600',
                  )}
                  onClick={closeMenu}
                  ref={$closeButtonRef}
                  type="button"
                >
                  <Close aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="mt-10">
              <nav
                aria-label="Glavna mobilna navigacija"
                className="grid gap-y-8"
              >
                {services.map((service) => (
                  <Link
                    aria-current={
                      currentPath === `/${service.href.current}`
                        ? 'page'
                        : undefined
                    }
                    className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-100"
                    key={service.href.current}
                    onClick={closeMenu}
                    to={`/${service.href.current}`}
                  >
                    <span
                      className={clsx(
                        'text-base font-medium',
                        currentPath === `/${service.href.current}`
                          ? 'text-blue-600'
                          : 'text-gray-900',
                      )}
                    >
                      {service.label}
                    </span>
                  </Link>
                ))}

                <Link
                  className="-m-3 flex items-start rounded-lg p-3 text-base font-medium text-gray-900 hover:bg-gray-100"
                  onClick={closeMenu}
                  to="/#kontakt"
                >
                  Kontakt
                </Link>

                <Link
                  className="-m-3 flex items-start rounded-lg p-3 text-base font-medium text-gray-900 hover:bg-gray-100"
                  onClick={closeMenu}
                  to="/projekti/"
                >
                  Referenc lista
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default MobileNavigation;
